import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  InjectionToken,
  computed,
  effect,
  inject,
  input,
  isDevMode,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Citation, CitationChip } from '@ucwip-frontend/ui-patterns';
import { AILabelModule } from 'carbon-components-angular/ai-label';
import { ButtonModule } from 'carbon-components-angular/button';
import { IconModule } from 'carbon-components-angular/icon';
import { InputModule } from 'carbon-components-angular/input';
import { ModalModule } from 'carbon-components-angular/modal';
import { NotificationModule } from 'carbon-components-angular/notification';
import { TagModule } from 'carbon-components-angular/tag';
import { WorkspaceRoleService } from '../../shell/workspace-role.service';
import { HotlineIntakeLauncher } from '../hotline-intake-launcher.service';
import {
  AI_ASSIST_RESPONSES,
  DEFAULT_AI_ASSIST_RESPONSE,
  FIXTURE_AI_QUICK_LAUNCH_AGENTS,
  FIXTURE_CALL_HISTORY,
  FIXTURE_QUICK_CONTACTS,
  FIXTURE_RECOMMENDED_ACTIONS,
  FIXTURE_TRANSCRIPT,
  FIXTURE_TRANSFER_TARGETS,
  INCOMING_CALL_FIXTURE,
} from './active-call-console.fixtures';
import {
  CALL_SESSION,
  CallSessionEvent,
  CallSessionState,
  FakeCallAdapter,
} from './call-session';

export type CallLifecycle =
  | 'idle'
  | 'incoming'
  | 'active'
  | 'ended'
  | 'transcription-error';
export type CallTab = 'dial' | 'active' | 'assist' | 'history';
export type Speaker = 'caller' | 'worker';
export type ActionCategory = 'Task' | 'Court' | 'Verify';

export interface TranscriptMessage {
  id: string;
  speaker: Speaker;
  speakerLabel: string;
  text: string;
}

export interface QuickContact {
  id: string;
  name: string;
  role: string;
  phone: string;
  initials: string;
}

export interface IncomingCallInfo {
  callId?: string;
  name: string;
  caseId: string | null;
  phone: string;
  location?: string;
  at?: string;
  direction: 'inbound' | 'outbound';
  initials: string;
  matched: boolean;
}

export interface RecommendedAction {
  id: string;
  description: string;
  category: ActionCategory;
  /** Absolute router commands — when set, clicking navigates as well as emitting. */
  routeCommands?: readonly string[];
}

export interface AiQuickLaunchAgent {
  id: string;
  name: string;
  description: string;
  iconGlyph: string;
}

export interface AiAssistResponse {
  summary: string;
  citations: readonly Citation[];
}

export interface CallHistoryEntry {
  id: string;
  name: string;
  direction: 'inbound' | 'outbound';
  durationLabel: string;
  timestampLabel: string;
  summary: string;
}

export interface TransferTarget {
  id: string;
  name: string;
  role: string;
}

const TRANSCRIPT_STREAM_INTERVAL_MS = 2200;
const AUTO_SCROLL_THRESHOLD_PX = 24;
const AI_QUERY_DELAY_MS = 900;

/**
 * Telephony is faked until a vendor adapter exists, so the panel needs a way
 * to ring itself locally. Production builds resolve this to false, keeping
 * the simulation control out of anything a stakeholder or evaluator sees.
 */
export const FAKE_CALL_CONTROLS_ENABLED = new InjectionToken<boolean>(
  'FakeCallControlsEnabled',
  { providedIn: 'root', factory: () => isDevMode() },
);

/**
 * B1 (UCWIP UI Build Spec, Section 2, Block B, v1.4): Active Call Console.
 *
 * Rebuilt against the corrected spec, which reassigns the pre-v1.4 file's
 * entire premise — a hotline-intake form with an "AI Intake Draft" panel and
 * duplicate-match cards — to B2. `IntakeDraftField` and `DuplicateMatchCard`
 * are deleted, not reused: B2 needs percentage confidence (not their
 * Tag-based display) and a plain duplicate-check list (not a card row), so
 * nothing about their shape carries forward. What *did* carry over, because
 * the technique is sound independent of screen shape: the elapsed-timer
 * computed+effect pattern, the transcript auto-scroll/jump-to-latest logic,
 * and streaming fixture messages onto the transcript via a setInterval
 * effect (still no transcription backend anywhere in this build). Incoming
 * pickup is wired through FakeCallAdapter so Answer moves ringing to active
 * without a vendor SDK or persist layer.
 *
 * A persistent, summonable panel (mounted once in the app shell — see
 * app.html), not a routed screen. `callState` seeds `lifecycle` once per
 * input change via `linkedSignal`; from then on the console's own actions
 * (dial, answer, end, transfer) drive it locally, matching how a real call
 * console owns its own state between the moments a caller rings in.
 */
@Component({
  selector: 'app-active-call-console',
  imports: [
    AILabelModule,
    ButtonModule,
    CitationChip,
    DatePipe,
    IconModule,
    InputModule,
    ModalModule,
    NotificationModule,
    TagModule,
  ],
  templateUrl: './active-call-console.html',
  styleUrl: './active-call-console.scss',
})
export class ActiveCallConsole {
  private readonly router = inject(Router);
  private readonly hotlineIntakeLauncher = inject(HotlineIntakeLauncher);
  private readonly callSession = inject(CALL_SESSION);
  private readonly fakeCallAdapter = inject(FakeCallAdapter);
  private readonly roleService = inject(WorkspaceRoleService);
  readonly callState = input<CallLifecycle>();
  readonly showFakeCallControls = input(false);
  readonly incomingCall = input<IncomingCallInfo>(INCOMING_CALL_FIXTURE);
  readonly quickContacts = input<readonly QuickContact[]>(
    FIXTURE_QUICK_CONTACTS,
  );
  readonly transcript = input<readonly TranscriptMessage[]>(FIXTURE_TRANSCRIPT);
  readonly recommendedActions = input<readonly RecommendedAction[]>(
    FIXTURE_RECOMMENDED_ACTIONS,
  );
  readonly quickLaunchAgents = input<readonly AiQuickLaunchAgent[]>(
    FIXTURE_AI_QUICK_LAUNCH_AGENTS,
  );
  readonly transferTargets = input<readonly TransferTarget[]>(
    FIXTURE_TRANSFER_TARGETS,
  );
  readonly callHistory =
    input<readonly CallHistoryEntry[]>(FIXTURE_CALL_HISTORY);
  readonly transcriptionErrorMessage = input(
    'The transcription service is not responding.',
  );

  readonly actionTriggered = output<string>();
  readonly transferCompleted = output<string>();

  protected readonly lifecycle = linkedSignal(() => this.callState() ?? 'idle');
  private readonly sessionCall = signal<CallSessionEvent | null>(null);

  // Open by default whenever a call is connected (matching every
  // Storybook/test state that sets `callState` directly, not just real
  // clicks) but resets on every lifecycle change, so a worker's manual
  // close doesn't linger and hide the console on the NEXT call. 'incoming'
  // stays closed — that state shows only the separate incoming-call toast
  // (answer/dismiss), matching the acceptance criteria's own phrasing.
  protected readonly panelOpen = linkedSignal<CallLifecycle, boolean>({
    source: this.lifecycle,
    computation: (lifecycle) =>
      lifecycle === 'active' ||
      lifecycle === 'ended' ||
      lifecycle === 'transcription-error',
  });

  // Defaults to the tab matching the current call lifecycle (active call ->
  // Active Call tab, ended -> History, otherwise Dial Pad) and resets to
  // that default every time `lifecycle` changes — but a manual tab click
  // (switchTab) overrides it until the lifecycle itself changes again. Using
  // linkedSignal here (rather than an effect) means this stays correct
  // synchronously on the very next read, including when `callState` is set
  // directly (Storybook args, tests) rather than through a user action.
  protected readonly activeTab = linkedSignal<CallLifecycle, CallTab>({
    source: this.lifecycle,
    computation: (lifecycle) => {
      // transcription-error occurs mid-call — the Active Call tab is still
      // the right place for it (manual notes stay usable per the
      // acceptance criteria), just with the transcript replaced by the
      // error notification.
      if (lifecycle === 'active' || lifecycle === 'transcription-error') {
        return 'active';
      }
      if (lifecycle === 'ended') {
        return 'history';
      }
      return 'dial';
    },
  });

  protected readonly dialBuffer = signal('');
  protected readonly dialError = signal<string | null>(null);
  protected readonly muted = signal(false);
  protected readonly onHold = signal(false);
  protected readonly completedActionIds = signal<ReadonlySet<string>>(
    new Set(),
  );
  protected readonly transferModalOpen = signal(false);
  protected readonly callNotes = signal('');

  protected readonly visibleMessageCount = signal(0);
  protected readonly isPinnedToBottom = signal(true);
  protected readonly now = signal(Date.now());

  protected readonly aiQuery = signal('');
  protected readonly aiResponse = signal<AiAssistResponse | null>(null);
  protected readonly aiAnalyzing = signal(false);

  protected readonly transcriptContainer =
    viewChild<ElementRef<HTMLDivElement>>('transcriptContainer');

  private readonly callStartedAt = signal<number | null>(null);
  /** True once the current active call has a start time. Not a signal — the clock effect must not restart when the timestamp is written. */
  private callClockRunning = false;
  private aiQueryTimer?: ReturnType<typeof setTimeout>;
  private lastAdapterState: CallSessionState | null = null;

  protected readonly displayedCall = computed<IncomingCallInfo>(() => {
    const fixture = this.incomingCall();
    const session = this.sessionCall();
    if (!session || session.state === 'ended') {
      return fixture;
    }
    // A session-driven call only inherits the fixture's case match when it is
    // genuinely the same number. Otherwise the banner would assert
    // "Record matched — Rosa Mendez · CS-2847" for an unrelated caller.
    const sameNumber = session.phone === fixture.phone;
    return {
      ...fixture,
      callId: session.callId,
      phone: session.phone,
      location: session.location,
      at: session.at,
      direction: session.direction,
      name: session.displayName ?? fixture.name,
      matched: sameNumber && fixture.matched,
      caseId: sameNumber ? fixture.caseId : null,
    };
  });

  protected readonly displayedTranscript = computed(() => {
    if (this.displayedCall().direction === 'outbound') {
      return [];
    }
    const messages = this.transcript();
    if (this.lifecycle() === 'active') {
      return messages.slice(0, this.visibleMessageCount());
    }
    if (this.lifecycle() === 'ended') {
      return messages;
    }
    return [];
  });

  protected readonly elapsedLabel = computed(() => {
    const startedAt = this.callStartedAt();
    this.now(); // depend on the ticking clock
    if (startedAt === null) {
      return '0:00';
    }
    const elapsedSeconds = Math.max(
      Math.floor((this.now() - startedAt) / 1000),
      0,
    );
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = (elapsedSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  });

  protected readonly callerMatchLabel = computed(() => {
    const call = this.displayedCall();
    return call.matched && call.caseId
      ? `Record matched — ${call.name} · ${call.caseId}`
      : null;
  });

  protected readonly outboundStartedLabel = computed(() => {
    const call = this.displayedCall();
    if (call.direction !== 'outbound' || !call.at) {
      return null;
    }
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(call.at));
  });
  /** Latest transcript line for screen-reader announcement (speaker + text). */
  protected readonly latestTranscriptAnnouncement = computed(() => {
    const lines = this.displayedTranscript();
    const latest = lines[lines.length - 1];
    return latest ? `${latest.speakerLabel}: ${latest.text}` : '';
  });

  protected readonly showOnCallTab = computed(
    () =>
      this.lifecycle() === 'active' ||
      this.lifecycle() === 'transcription-error',
  );

  constructor() {
    effect(() => {
      if (this.callState() !== undefined) {
        return;
      }

      const session = this.callSession.session();
      const previousState = this.lastAdapterState;
      this.lastAdapterState = session.state;
      this.sessionCall.set(session);

      if (session.state === 'ringing') {
        this.lifecycle.set('incoming');
      } else if (session.state === 'answered') {
        this.lifecycle.set('active');
      } else {
        this.lifecycle.set(previousState === 'answered' ? 'ended' : 'idle');
      }
    });

    // Streams one more fixture message onto the transcript every tick while
    // the call is active, simulating incoming speech with no real backend.
    effect((onCleanup) => {
      if (this.lifecycle() !== 'active') {
        return;
      }
      const total = this.transcript().length;
      const timer = setInterval(() => {
        this.visibleMessageCount.update((count) => Math.min(count + 1, total));
      }, TRANSCRIPT_STREAM_INTERVAL_MS);
      onCleanup(() => clearInterval(timer));
    });

    // Call clock. Each transition into an active call starts at 0:00.
    // callStartedAt is a signal so the label cannot keep the previous
    // call's duration until the next tick. A later callback to the same
    // number must not reuse that start time.
    effect((onCleanup) => {
      const lifecycle = this.lifecycle();
      if (lifecycle !== 'active') {
        if (lifecycle !== 'transcription-error') {
          this.callStartedAt.set(null);
          this.callClockRunning = false;
        }
        return;
      }
      if (!this.callClockRunning) {
        this.resetCallClock();
      }
      const timer = setInterval(() => this.now.set(Date.now()), 1000);
      onCleanup(() => clearInterval(timer));
    });

    // Auto-scrolls the transcript to the newest message, unless the reader
    // has scrolled up to review earlier messages (isPinnedToBottom false).
    effect(() => {
      this.displayedTranscript();
      if (this.isPinnedToBottom()) {
        this.scrollTranscriptToBottom();
      }
    });
  }

  /**
   * Resets per-call UI state (matching occStartCall/occAnswerCall in the
   * prototype). `activeTab` doesn't need resetting here — it's a
   * `linkedSignal` derived from `lifecycle`, so switching `lifecycle` to
   * 'active' already moves it to the Active Call tab on its own.
   */
  private startActiveCallUiState(): void {
    this.visibleMessageCount.set(0);
    this.muted.set(false);
    this.onHold.set(false);
    this.callNotes.set('');
    this.resetCallClock();
  }

  /** Anchors the duration label at 0:00 for the call that just connected. */
  private resetCallClock(): void {
    const startedAt = Date.now();
    this.callStartedAt.set(startedAt);
    this.now.set(startedAt);
    this.callClockRunning = true;
  }

  protected togglePanel(): void {
    this.panelOpen.update((open) => !open);
  }

  protected closePanel(): void {
    this.panelOpen.set(false);
  }

  protected simulateIncomingCall(): void {
    this.fakeCallAdapter.simulateIncoming();
  }

  protected switchTab(tab: CallTab): void {
    this.panelOpen.set(true);
    this.activeTab.set(tab);
  }

  protected dialKey(key: string): void {
    this.dialError.set(null);
    this.dialBuffer.update((value) => value + key);
  }

  protected dialBackspace(): void {
    this.dialError.set(null);
    this.dialBuffer.update((value) => value.slice(0, -1));
  }

  protected startManualCall(): void {
    const typedPhone = this.dialBuffer().trim();
    if (typedPhone) {
      this.startCallback(typedPhone);
      return;
    }
    this.startCallback(this.incomingCall().phone.trim());
  }

  protected dialContact(contact: QuickContact): void {
    this.dialBuffer.set(contact.phone);
    this.startCallback(contact.phone);
  }

  private startCallback(phone: string): void {
    if (!this.roleService.canUseCallPanel()) {
      return;
    }
    if (!this.isUsablePhoneNumber(phone)) {
      this.dialError.set(
        phone
          ? 'Enter a valid phone number before starting a callback.'
          : 'Enter a phone number before starting a callback.',
      );
      return;
    }

    this.dialError.set(null);
    this.callSession.startOutbound({ phone });
    const session = this.callSession.session();
    if (session.direction !== 'outbound' || session.state !== 'answered') {
      return;
    }
    this.sessionCall.set(session);
    this.dialBuffer.set(session.phone);
    this.lifecycle.set('active');
    this.startActiveCallUiState();
  }

  private isUsablePhoneNumber(phone: string): boolean {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15;
  }

  protected answerIncoming(): void {
    this.dialError.set(null);
    const callId = this.displayedCall().callId;
    if (callId) {
      this.callSession.answer(callId);
    }
    this.lifecycle.set('active');
    this.startActiveCallUiState();
  }

  protected dismissIncoming(): void {
    const callId = this.displayedCall().callId;
    if (callId) {
      this.callSession.end(callId);
    }
    this.lifecycle.set('idle');
  }

  protected toggleMute(): void {
    this.muted.update((value) => !value);
  }

  protected toggleHold(): void {
    this.onHold.update((value) => !value);
  }

  protected openTransferModal(): void {
    this.transferModalOpen.set(true);
  }

  protected cancelTransfer(): void {
    this.transferModalOpen.set(false);
  }

  protected confirmTransfer(target: TransferTarget): void {
    this.transferModalOpen.set(false);
    this.transferCompleted.emit(target.id);
    this.lifecycle.set('ended');
  }

  protected endCall(): void {
    const callId = this.displayedCall().callId;
    if (callId) {
      this.callSession.end(callId);
    }
    this.lifecycle.set('ended');
  }

  protected onTranscriptScroll(): void {
    const el = this.transcriptContainer()?.nativeElement;
    if (!el) {
      return;
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.isPinnedToBottom.set(distanceFromBottom < AUTO_SCROLL_THRESHOLD_PX);
  }

  protected scrollToLatest(): void {
    this.isPinnedToBottom.set(true);
    this.scrollTranscriptToBottom();
  }

  private scrollTranscriptToBottom(): void {
    const el = this.transcriptContainer()?.nativeElement;
    // jsdom (unit tests) doesn't implement scrollTo at all — guard the call
    // itself, not just the element, so tests can exercise this logic too.
    el?.scrollTo?.({ top: el.scrollHeight, behavior: 'smooth' });
  }

  protected isActionCompleted(id: string): boolean {
    return this.completedActionIds().has(id);
  }

  protected triggerAction(action: RecommendedAction): void {
    this.completedActionIds.update((set) => new Set(set).add(action.id));
    this.actionTriggered.emit(action.id);
    if (action.routeCommands?.length) {
      void this.router.navigate([...action.routeCommands]);
    }
  }
  /** Opens B2 on IntakeDashboardPage — the only Hotline Intake modal in the tree. */
  protected startHotlineIntake(): void {
    const call = this.displayedCall();
    const context =
      call.callId && call.at
        ? {
            callId: call.callId,
            phone: call.phone,
            at: call.at,
            direction: call.direction,
            ...(call.location ? { location: call.location } : {}),
          }
        : undefined;

    this.hotlineIntakeLauncher.requestOpen(context);
    if (this.router.url !== '/dashboard') {
      void this.router.navigateByUrl('/dashboard');
    }
  }

  protected onCallNotesInput(event: Event): void {
    this.callNotes.set((event.target as HTMLTextAreaElement).value);
  }

  protected onAiQueryInput(event: Event): void {
    this.aiQuery.set((event.target as HTMLInputElement).value);
  }

  protected launchAgent(agent: AiQuickLaunchAgent): void {
    this.runAiQuery(agent.id);
  }

  protected submitAiQuery(): void {
    const query = this.aiQuery().trim();
    if (!query) {
      return;
    }
    this.runAiQuery(query.toLowerCase());
  }

  private runAiQuery(key: string): void {
    clearTimeout(this.aiQueryTimer);
    this.aiAnalyzing.set(true);
    this.aiResponse.set(null);
    this.aiQueryTimer = setTimeout(() => {
      const matchedKey = Object.keys(AI_ASSIST_RESPONSES).find((candidate) =>
        key.includes(candidate),
      );
      this.aiResponse.set(
        matchedKey
          ? AI_ASSIST_RESPONSES[matchedKey]
          : DEFAULT_AI_ASSIST_RESPONSE,
      );
      this.aiAnalyzing.set(false);
      this.aiQuery.set('');
    }, AI_QUERY_DELAY_MS);
  }
}
